// ── Adapter Supabase do IAuthGateway ──────────────────────────────
// Única camada que conhece supabase.auth. Converte o { data, error } do
// Supabase no contrato tipado da porta.

import { getSupabase } from '@infra/supabase/client';
import type { IAuthGateway, AuthResult, SignUpResult, AuthUser } from '@modules/auth';

const requireSupabase = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

export class SupabaseAuthGateway implements IAuthGateway {
  signUp = async ({ email, password }: { email: string; password: string }): Promise<SignUpResult> => {
    const { data, error } = await requireSupabase().auth.signUp({ email, password });
    return { error: error?.message ?? null, hasSession: !!data?.session };
  };

  signInWithPassword = async ({ email, password }: { email: string; password: string }): Promise<AuthResult> => {
    const { error } = await requireSupabase().auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  setSession = async ({ access_token, refresh_token }: { access_token: string; refresh_token: string }): Promise<AuthResult> => {
    const { error } = await requireSupabase().auth.setSession({ access_token, refresh_token });
    return { error: error?.message ?? null };
  };

  getSession = async (): Promise<{ hasSession: boolean }> => {
    const { data } = await requireSupabase().auth.getSession();
    return { hasSession: !!data?.session };
  };

  updateUser = async (attrs: { password?: string }): Promise<AuthResult> => {
    const { error } = await requireSupabase().auth.updateUser(attrs);
    return { error: error?.message ?? null };
  };

  getCurrentUser = async (): Promise<AuthUser | null> => {
    const { data } = await requireSupabase().auth.getSession();
    const u = data?.session?.user;
    return u ? { id: u.id, email: u.email ?? null } : null;
  };

  onAuthStateChange = (cb: (user: AuthUser | null) => void): (() => void) => {
    const supabase = getSupabase();
    if (!supabase) return () => {};
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return;
      const u = session?.user;
      cb(u ? { id: u.id, email: u.email ?? null } : null);
    });
    return () => data.subscription.unsubscribe();
  };

  signOut = async (): Promise<void> => {
    await requireSupabase().auth.signOut();
  };
}
